import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-824e420c"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "panel" }
const _hoisted_2 = { class: "panel" }

import { computed, ref, inject, watch } from "vue";
import modalBase from "@/components/ui/modals/modal-base.vue";
import { ModalWidth } from "@/types/ui/modals/ModalWidth";
import { DxToolbarItem } from "devextreme-vue/popup";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import { AcquaintApiNextClient } from "@/services/AcquaintApiNextClient";
import displayErrorMessage from "@/utils/ErrorHandling";
import { ValidatedEvent } from "devextreme/ui/validator";
import { FieldSize } from "@/enums/system/FieldSize";
import SwitchToggle from "../ui/toggles/switch-toggle.vue";
import AcquaintTextbox from "@/components/ui/inputs/acquaint-textbox.vue";
import { EmailSignaturesValidation } from "acquaint-api/validation/EmailSignaturesValidation";
import { EmailSignatures } from "acquaint-api/models/EmailSignatures";
import { DxTabPanel, DxItem } from "devextreme-vue/tab-panel";
import HtmlEditor from "../html-editor/html-editor.vue";
import MergeFieldsModal from "../contacts/merge-fields-modal.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'new-edit-email-signatures-modal',
  props: {
  /**
   * The boolean value used with `v-model` to control the visibility or state of a modal or component.
   * @type {boolean}
   * @required
   */
  modelValue: {
    type: Boolean,
    required: true,
  },
  /**
   * The unique identifier for the template.
   * @type {number}
   * @required
   */
  configurationId: {
    type: Number,
    required: true,
  },
},
  emits: [
  /**
   * Triggered when the `modelValue` changes. This is used to update the v-model variable in the parent component.
   * @property {boolean} value - Indicates whether the modal has been opened or closed.
   */
  "update:modelValue",
  /**
   * Emitted when an existing template configuration  has been successfully edited.
   * This notifies the parent component to refresh or update the template configuration details.
   * @property {boolean} inActive - Indicates whether the template configuration is inactive after the edit.
   */
  "configurationItemEdited",
  /**
   * Emitted when a new template configuration has been successfully created.
   * This notifies the parent component to handle the creation of a new template configuration.
   */
  "configurationItemCreated",
],
  setup(__props, { emit }) {

const props = __props;





const showModal = computed({
  get: () => props.modelValue,
  set: (value: Boolean) => emit("update:modelValue", value),
});

const acquaintApiClient = inject("AcquaintApiClient") as AcquaintApiClient;
const acquaintApiNextClient = inject(
  "AcquaintApiNextClient",
) as AcquaintApiNextClient;
const originalItemData = ref<EmailSignatures>();
const itemData = ref<EmailSignatures>();
const lockedByAnotherUser = ref(false);
const keepingModalOpening = ref(false);
const submissionResponse = ref<EmailSignatures>();
const selectedTab = ref(0);
const openMergeFieldsModal = ref(false);
const footerMergeFieldToAdd = ref("");
const headerMergeFieldToAdd = ref("");
const displayCancelConfirmationModal = ref(false);

const lockConfigurationItem = async () => {
  try {
    await acquaintApiClient.httpPostVoid(
      `database/emailsignatures/${props.configurationId}/lock`,
      { lock: true, extraInfo: "" },
    );
  } catch (err) {
    lockedByAnotherUser.value = true;
    displayErrorMessage(
      "These details are already being edited by another user.",
    );
  }
};

const unLockConfigurationItem = async () => {
  try {
    await acquaintApiClient.httpPostVoid(
      `database/emailsignatures/${props.configurationId}/lock`,
      { lock: false, extraInfo: "" },
    );
  } catch (err) {
    displayErrorMessage("We were unable to unlock these details.");
  }
};

const fetchItemData = async () => {
  try {
    if (props.configurationId == 0) {
      originalItemData.value = await acquaintApiClient.httpGet<EmailSignatures>(
        `database/emailsignatures/defaultmodel`,
      );
    } else {
      originalItemData.value =
        await acquaintApiNextClient.httpGet<EmailSignatures>(
          `EmailSignatures/${props.configurationId}`,
        );
    }
  } catch (err) {
    originalItemData.value = undefined;
    itemData.value = undefined;
    if (props.configurationId == 0) {
      displayErrorMessage("The selected details could not be fetched");
    } else {
      displayErrorMessage("We could not fetch the default data");
    }
  }
};

watch(
  () => itemData.value,
  () => {
    displayCancelConfirmationModal.value =
      JSON.stringify(originalItemData.value) != JSON.stringify(itemData.value);
  },
  { deep: true },
);

watch(
  () => showModal.value,
  async () => {
    if (keepingModalOpening.value) {
      return;
    }

    if (showModal.value) {
      lockedByAnotherUser.value = false;
      selectedTab.value = 0;
      displayCancelConfirmationModal.value = false;

      await fetchItemData();
      if (originalItemData.value) {
        itemData.value = JSON.parse(JSON.stringify(originalItemData.value));
      }

      if (props.configurationId == 0) {
        return;
      }

      await lockConfigurationItem();
    }
  },
);

const saveDetails = async (e: any) => {
  const validation: ValidatedEvent = e.validationGroup.validate();
  // Don't continue if user has entered invalid data
  if (!validation.isValid) {
    return;
  }

  if (!(await callSaveApi())) {
    return;
  }

  lockedByAnotherUser.value = true;
  displayCancelConfirmationModal.value = false;

  if (props.configurationId > 0) {
    await unLockConfigurationItem();
    emit("configurationItemEdited", itemData.value?.inactive);
  } else {
    emit("configurationItemCreated");
  }

  showModal.value = false;
};

const callSaveApi = async () => {
  try {
    if (props.configurationId == 0) {
      submissionResponse.value =
        await acquaintApiClient.httpPost<EmailSignatures>(
          `database/emailsignatures`,
          itemData.value,
        );
    } else {
      submissionResponse.value =
        await acquaintApiClient.httpPut<EmailSignatures>(
          `database/emailsignatures/${props.configurationId}`,
          itemData.value,
        );
    }
    return true;
  } catch (err) {
    if (props.configurationId == 0) {
      displayErrorMessage("A new system item could not be created.");
    } else {
      displayErrorMessage("The selected details could not be edited.");
    }
    return false;
  }
};
const cancelClick = () => {
  showModal.value = false;
};

const keepEnteredInformation = () => {
  keepingModalOpening.value = true;
};

const cancelPromptDenied = () => {
  keepingModalOpening.value = true;
};

const modalClosed = async () => {
  if (props.configurationId > 0) {
    await unLockConfigurationItem();
  }
  keepingModalOpening.value = false;
};

const insertField = () => {
  openMergeFieldsModal.value = true;
};

const addMergeField = (mergeField: String) => {
  if (!itemData.value) {
    return;
  }

  if (selectedTab.value == 0) {
    footerMergeFieldToAdd.value = `[${mergeField}]`;
    setTimeout(() => {
      footerMergeFieldToAdd.value = "";
    }, 250);
  } else {
    headerMergeFieldToAdd.value = `[${mergeField}]`;
    setTimeout(() => {
      headerMergeFieldToAdd.value = "";
    }, 250);
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(modalBase, {
      title: "Email Signatures",
      modelValue: showModal.value,
      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((showModal).value = $event)),
      showCloseButton: true,
      showCloseConfirmationPrompt: displayCancelConfirmationModal.value,
      height: "650px",
      onCancelConfirmationModal: keepEnteredInformation,
      onModalClosed: modalClosed,
      width: _unref(ModalWidth).Small,
      onCancelPromptDenied: cancelPromptDenied
    }, {
      content: _withCtx(() => [
        (itemData.value)
          ? (_openBlock(), _createBlock(_unref(DxTabPanel), {
              key: 0,
              "scroll-by-content": true,
              "show-nav-buttons": true,
              "swipe-enabled": false,
              selectedIndex: selectedTab.value,
              "onUpdate:selectedIndex": _cache[4] || (_cache[4] = ($event: any) => ((selectedTab).value = $event))
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(DxItem), { title: "Footer" }, {
                  default: _withCtx(() => [
                    _createElementVNode("section", _hoisted_1, [
                      _createVNode(AcquaintTextbox, {
                        fieldValidation: _unref(EmailSignaturesValidation).Description,
                        label: "Description",
                        modelValue: itemData.value.description,
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((itemData.value.description) = $event)),
                        width: _unref(FieldSize).Large,
                        readOnly: lockedByAnotherUser.value,
                        validationGroup: "EmailSignaturesValidationGroup"
                      }, null, 8, ["fieldValidation", "modelValue", "width", "readOnly"]),
                      _createVNode(SwitchToggle, {
                        label: "Inactive",
                        modelValue: itemData.value.inactive,
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((itemData.value.inactive) = $event)),
                        readOnly: lockedByAnotherUser.value
                      }, null, 8, ["modelValue", "readOnly"]),
                      _createVNode(HtmlEditor, {
                        label: "Footer",
                        modelValue: itemData.value.body,
                        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((itemData.value.body) = $event)),
                        readOnly: lockedByAnotherUser.value,
                        insertTextAtCursorPosition: footerMergeFieldToAdd.value
                      }, null, 8, ["modelValue", "readOnly", "insertTextAtCursorPosition"])
                    ])
                  ]),
                  _: 1
                }),
                _createVNode(_unref(DxItem), { title: "Header" }, {
                  default: _withCtx(() => [
                    _createElementVNode("section", _hoisted_2, [
                      _createVNode(HtmlEditor, {
                        label: "Header",
                        modelValue: itemData.value.header,
                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((itemData.value.header) = $event)),
                        readOnly: lockedByAnotherUser.value,
                        insertTextAtCursorPosition: headerMergeFieldToAdd.value
                      }, null, 8, ["modelValue", "readOnly", "insertTextAtCursorPosition"])
                    ])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["selectedIndex"]))
          : _createCommentVNode("", true)
      ]),
      footer: _withCtx(() => [
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "before",
          options: {
          text: 'Insert Field',
          onClick: () => {
            insertField();
          },
        }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'Save',
          disabled: lockedByAnotherUser.value,
          onClick: (e) => {
            saveDetails(e);
          },
          validationGroup: 'EmailSignaturesValidationGroup',
        }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'Cancel',
          onClick: () => {
            cancelClick();
          },
        }
        }, null, 8, ["options"])
      ]),
      _: 1
    }, 8, ["modelValue", "showCloseConfirmationPrompt", "width"]),
    _createVNode(MergeFieldsModal, {
      modelValue: openMergeFieldsModal.value,
      "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((openMergeFieldsModal).value = $event)),
      onAddMergeField: addMergeField
    }, null, 8, ["modelValue"])
  ], 64))
}
}

})